<!-- DashbaordRefCoach.vue -->
<template>
  <!-- Loading Modal -->
  <div v-if="isLoading" class="loading_modal">
    <div class="modal">
      <p>Loading data...</p>
      <div class="spinner"></div>
    </div>
  </div>

  <div class="pageheader">REFCOACH Report Management</div>
  <div class="mainlayout">
    <div>
      <button class="button_addreport" @click="openModal">Add Report</button>
    </div>

    <!-- Modal Popup -->
    <div v-if="showModal" class="modal-overlay">
      <div class="modal">
        <h3>Create match report for the following Referee</h3>
        <div class="form-group">
          <label for="userDropdown">Select Referee:</label>
          <select id="userDropdown" v-model="selectedUserId">
            <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name }}</option>
          </select>
        </div>
        <div class="modal-buttons">
          <button @click="addMatchReport">Create</button>
          <button @click="cancelModal">Cancel</button>
        </div>
      </div>
    </div>

    <div class="RL_note">
      List of loaded match reports. Click on "Add Report" to add new reports. Select a report to update/delete.
    </div>

    <div class="gridContainer">
      <div class="gridHeaders">
        <div class="gridItem name">Name</div>
        <div class="gridItem gamedate">Date</div>
        <div class="gridItem gamenumber">Game Number</div>
        <div class="gridItem leaguetypeseason">LeagueTypeSeason</div> 
        <div class="gridItem genderage">GenderAge</div>
        <div class="gridItem teams">Teams: Home vs. Away</div>
        <div class="gridItem yellowcard">YC</div>
        <div class="gridItem redcard">RC</div>
        <div class="gridItem actions">Actions</div>
      </div>
      <div class="gridContent">
        <ul>
          <li v-for="mreport in matchreports" :key="mreport.id" class="gridRow" @click="editMatchReport(mreport.id)">
            <div class="gridItem name">{{ mreport.name }}</div>
            <div class="gridItem gamedate">{{ mreport.GameDate }}</div>
            <div class="gridItem gamenumber">G{{ mreport.GameNumber }}</div>
            <div class="gridItem leaguetypeseason">{{ mreport.LeagueTypeSeason }}</div>  
            <div class="gridItem genderage">{{ mreport.GenderAge }}</div>
            <div class="gridItem teams">{{ mreport.TeamHome }} vs. {{ mreport.TeamAway }}</div>
            <div class="gridItem yellowcard">
              <div v-if="mreport.YellowCardCount > 0" class="card-count yellow">{{ mreport.YellowCardCount }}</div>
            </div>
            <div class="gridItem redcard">
              <div v-if="mreport.RedCardCount > 0" class="card-count red">{{ mreport.RedCardCount }}</div>
            </div>
            <div class="gridItem actions">
              <span class="material-icons edit" @click.stop="editMatchReport(mreport.id)">edit</span>
              <span class="material-icons delete" @click.stop="deleteMatchReport(mreport.id)">delete</span>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { db, matchreportsColRef, refereesColRef, misconductColRef } from "../firebase";
import { query, orderBy, where, getDocs, deleteDoc, doc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useRouter } from "vue-router";
import { ref, onMounted } from "vue"; 

export default {
  name: 'DashboardRefCoach',
  setup() {
    const matchreports = ref([]);
    const router = useRouter();
    const isLoading = ref(true);
    const users = ref([]);
    const selectedUserId = ref(null);
    const showModal = ref(false);

    onMounted(async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        console.log("User is not authenticated");
        router.push("/login");
        return;
      }

      // ✅ FIX: Validate user access before loading data
      try {
        await validateUserAccess(user);
      } catch (error) {
        console.error("Access validation failed:", error);
        alert(error.message);
        router.push("/login");
        return;
      }

      await loadMatchReports();
      isLoading.value = false;
    });

    // ✅ NEW: Validate user has proper access
    async function validateUserAccess(user) {
      // Force refresh token to get latest claims
      await user.getIdToken(true);
      const idTokenResult = await user.getIdTokenResult(true);
      const claims = idTokenResult.claims;

      console.log("DashboardRefCoach - Validating claims:", claims);

      // Check if claims exist
      if (!claims || !claims.roles) {
        throw new Error("No access roles found. Please contact administrator.");
      }

      // Get stored org and role from localStorage
      const storedOrg = localStorage.getItem("currentUserOrgName");
      const storedRole = localStorage.getItem("currentUserRole");

      if (!storedOrg || !storedRole) {
        throw new Error("Session data missing. Please log in again.");
      }

      // Validate user has access to the stored organization
      const rolesMap = claims.roles || {};
      const userRoleForOrg = rolesMap[storedOrg];

      if (!userRoleForOrg) {
        const availableOrgs = Object.keys(rolesMap).join(", ") || "(none)";
        throw new Error(
          `You no longer have access to "${storedOrg}". Available organizations: ${availableOrgs}`
        );
      }

      // ✅ Validate user is RefereeCoach or ADMIN
      if (userRoleForOrg !== "RefereeCoach" && userRoleForOrg !== "ADMIN") {
        throw new Error(
          `This dashboard requires RefereeCoach or ADMIN role. Your role for "${storedOrg}" is "${userRoleForOrg}".`
        );
      }

      // Validate role matches
      if (userRoleForOrg !== storedRole) {
        console.warn(
          `Role mismatch: stored="${storedRole}", actual="${userRoleForOrg}". Updating...`
        );
        localStorage.setItem("currentUserRole", userRoleForOrg);
      }

      // ✅ Check token expiration
      const expirationTime = new Date(idTokenResult.expirationTime);
      const now = new Date();
      const timeUntilExpiry = expirationTime - now;

      console.log(`Token expires in ${Math.floor(timeUntilExpiry / 1000 / 60)} minutes`);

      // If token expires in less than 5 minutes, refresh it
      if (timeUntilExpiry < 5 * 60 * 1000) {
        console.log("Token expiring soon, refreshing...");
        await user.getIdToken(true);
      }

      return true;
    }

    const loadMatchReports = async () => {
      try {
        const refereecoachOrgName = getRefereeCoachOrgName();
        if (!refereecoachOrgName) return;

        const q = query(
          matchreportsColRef,
          where("Organization", "==", refereecoachOrgName),
          orderBy("GameDate", "desc")
        );

        const querySnapshot = await getDocs(q);
        const matchReportsWithUserNames = [];

        for (const docSnapshot of querySnapshot.docs) {
          const reportData = docSnapshot.data();
          const userID = reportData.UserID;
          
          const refereeQuery = query(refereesColRef, where("UserID", "==", userID));
          const refereeQuerySnapshot = await getDocs(refereeQuery);

          // Ensure userID exists before querying referees
          if (!userID) {
            console.warn(`Match report ${docSnapshot.id} is missing a userID.`);
            continue;
          }

          let userName = "Unknown";
          if (!refereeQuerySnapshot.empty) {
            const refereeData = refereeQuerySnapshot.docs[0].data();
            userName = `${refereeData.NameFirst || ""} ${refereeData.NameLast || ""}`.trim();
          }

          matchReportsWithUserNames.push({
            id: docSnapshot.id,
            ...reportData,
            name: userName,
            GameDate: reportData.GameDate?.toDate().toLocaleDateString() || "N/A",
          });
        }

        matchreports.value = matchReportsWithUserNames;

      } catch (error) {
        console.error("Error fetching match reports:", error);
      }
    };

    const getRefereeCoachOrgName = () => {
      const orgName = localStorage.getItem("currentUserOrgName");
      if (!orgName) {
        console.error("Organization name is missing from localStorage");
        return null;
      }
      return orgName;
    };

    const openModal = async () => {
      await loadUsers();
      showModal.value = true;
    };

    const loadUsers = async () => {
      try {
        const refereecoachOrgName = getRefereeCoachOrgName();
        if (!refereecoachOrgName) {
           console.error("RefereeCoach Organization is undefined or missing. Cannot fetch match reports.");
          return;
        };

        const querySnapshot = await getDocs(
          query(
            refereesColRef, 
            where("Organization", "==", refereecoachOrgName),
            where("UserID", "!=", null) // Ensures UserID exists (not null or undefined)
          )
        );
        const usersList = querySnapshot.docs.map(doc => ({
          id: doc.data().UserID,
          name: `${doc.data().NameFirst || ""} ${doc.data().NameLast || ""}`.trim(),
          NameLast: doc.data().NameLast || "",
        }));

        users.value = usersList.sort((a, b) => a.NameLast.localeCompare(b.NameLast));
      } catch (error) {
        console.error("Error fetching referees:", error);
      }
    };

    const editMatchReport = (id) => 
      router.push({ 
        name: "MatchReport", 
        params: { id } 
      });

    const addMatchReport = async () => {
      try {
        if (!selectedUserId.value) return alert("Please select a referee.");

        router.push({ 
          name: "MatchReport", 
          params: { id: "NEW" },
          query: { selectedUserId: selectedUserId.value } 
        });
      } catch (error) {
        console.error("Error adding new report:", error);
      }
    };

    const deleteMatchReport = async (id) => {
      if (!confirm("Are you sure you want to delete this report?")) return;

      try {
        // Step 1: Find all misconduct reports linked to this match report
        const misconductQuery = query(misconductColRef, where("MatchReportID", "==", id));
        const misconductSnapshot = await getDocs(misconductQuery);

        if (misconductSnapshot.empty) {
          console.log("🟢 No misconduct reports found. Proceeding with match report deletion.");
        } else {
          console.log(`🔴 Found ${misconductSnapshot.size} misconduct reports. Deleting them first...`);

          // Step 2: Delete each misconduct report
          const deletePromises = misconductSnapshot.docs.map((docSnap) => {
            console.log(`🛑 Deleting misconduct report ID: ${docSnap.id}`);
            return deleteDoc(doc(db, "misconduct", docSnap.id));
          });

          await Promise.all(deletePromises);
          console.log("✅ All misconduct reports deleted.");
        }

        // Step 3: Now delete the match report
        console.log(`🟡 Deleting match report with ID: ${id}`);
        await deleteDoc(doc(db, "matchreports", id));

        // Step 4: Update UI to remove the deleted report from the list
        matchreports.value = matchreports.value.filter(report => report.id !== id);
        console.log("✅ Match report successfully deleted.");

      } catch (error) {
        console.error("❌ Error deleting match report and misconduct reports:", error);
      }
    };

    return {
      matchreports,
      isLoading,
      users,
      selectedUserId,
      showModal,
      openModal,
      editMatchReport,
      addMatchReport,
      deleteMatchReport,
      loadMatchReports,
    };
  },
};
</script>


<style scoped>
/* Styling for the form and report list */
body {
background: #fff2e6;
}
.pageheader {
position: fixed;  display: flex;
top: 70px;  left:0px;  height: 30px; width: 100%;
margin: 00px; padding: 10px;  
border-top: 5px; border-top: solid;  border-top-color: #02b8cc;
border-bottom: 1px;  border-bottom: solid;  border-bottom-color: #32e3f7;
background-color: #005ce6;  color: white;
font-size: x-large;
text-indent: 140px;
/*  justify-content: center; */
}
.button_addreport {
position: fixed; top: 138px;  left: 90px; height: 30px;  width: 150px;  
box-sizing: border-box;
padding: 6px 15px;
border-radius: 12px;  background-color: #99cc00; box-shadow:0 4px 8px 0 rgba(0,0,0,0.2), 0 6px 20px 0 rgba(0,0,0,0.19);
font-size: medium ; color: #000099; vertical-align: middle;
}
.RL_note {
position: fixed;
top:145px;  left: 270px;
}

/*--REPORT FORMATING -----------*/
/* Set font size for list items */
.gridContainer {
  position: fixed;
  top:180px;  margin-left: 100px;  margin-right: 20px;  
  max-width: 1300px;
  overflow-y: auto;
  scroll-behavior: smooth;
  font-size: 11px;
  height: 70vh; /* Adjust the height as needed */
}
.gridHeaders {
display: grid;
grid-template-columns: 175px 100px 100px 250px 75px 375px 30px 30px 75px;
gap: 5px;
padding: 5px;
background-color: #e9ecef;
margin-bottom: 4px; 
}
.gridContent ul {
  list-style-type: none; padding: 0; margin: 0;
}
.gridRow {
  display: grid; 
  grid-template-columns: 175px 100px 100px 250px 75px 380px 30px 30px 75px;
  gap: 5px;
  padding: 0px; margin-bottom: 4px; border-radius: 10px; background: #fff; font-size: 10pt;
  height: 30px;
}
.gridItem {
  padding: 5px;
}
/* Specific alignment for certain items */
.gridItem.name {justify-self: start;}
.gridItem.gamedate {justify-self: start;}
.gridItem.gamenumber { justify-self: center;}
.gridItem.leaguetypeseason {justify-self:left;}
.gridItem.genderage {justify-self: center;}
.gridItem.teams {justify-self: start;}
.gridItem.yellowcard {justify-self: center;}
.gridItem.redcard {justify-self: center;}
.gridItem.actions {justify-self: center;}

.gridContainer li:hover {
cursor: pointer;
background: #e6f5ff;
}

.material-icons {
cursor: pointer;
font-size: 18px;
color: #888;
}

.material-icons:hover {
color: #005ce6; /* Highlight edit on hover */
}

.material-icons.delete:hover {
color: red; /* Highlight delete on hover */
}
/* Styles for card counts */ 
.card-count { 
  display: flex; justify-content: center;  align-items: center;
  width: 20px; height: 20px; 
  border-radius: 35%; border: 1px solid black;
  font-size: 14px; color: white; font-weight: bold;
}
.card-count.yellow { background-color: yellow; color: black; }
.card-count.red { background-color: red; color: white; }

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000; /* Ensure it's above other content */
}

.modal {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
  z-index: 1001; /* Ensure modal is above overlay */
}

.modal h3 {
  text-align: center;
}

.form-group {
  margin-bottom: 15px;
}

select {
  width: 100%;
  padding: 10px;
  font-size: 16px;
}

.modal-buttons {
  display: flex;
  justify-content: space-between;
}

.modal-buttons button {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.modal-buttons button:hover {
  background-color: #e0e0e0;
}

button {
  background-color: #005ce6;
  color: white;
  border-radius: 5px;
  padding: 10px 15px;
}

button:hover {
  background-color: #003f9a;
}
/* Modal Overlay */
.loading_modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Modal */
.modal {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 200px;
  text-align: center;
}

/* Spinner for loading animation */
.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

/* Spinner animation */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
